import { useEffect, useMemo, useRef, useState } from "react";

// Shared components: Logo, Icon, Sidebar, Topbar, Layout, charts, etc.

/* ─── Logo: compass rose mark ────────────────────────────────────────────── */
function Logo({ size = 28, className = "" }) {
  // 4 petals + N/S/E/W ticks. Simple, geometric.
  return (
    <span className={`logo ${className}`} style={{ width: size, height: size }} aria-label="Community Compass">
      <svg viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
        {/* outer ring */}
        <circle cx="16" cy="16" r="14.5" stroke="currentColor" strokeOpacity="0.18" strokeWidth="1" />
        {/* compass rose — 4 diamonds rotated, north filled with accent */}
        <path d="M16 3 L19 16 L16 29 L13 16 Z" fill="var(--accent)" />
        <path d="M29 16 L16 19 L3 16 L16 13 Z" fill="currentColor" fillOpacity="0.85" />
        {/* small diagonal accents */}
        <path d="M16 9 L17.4 16 L16 23 L14.6 16 Z" fill="var(--surface)" fillOpacity="0.85" />
        {/* center dot */}
        <circle cx="16" cy="16" r="1.6" fill="var(--surface)" />
        <circle cx="16" cy="16" r="0.6" fill="currentColor" />
      </svg>
    </span>
  );
}

/* ─── Community switcher (shows when user has >1 membership) ─────────────── */
function CommunitySwitcher({ memberships, currentName, onPick }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div className="side__switcher" ref={rootRef} style={{ position: "relative" }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        title="Switch community"
        style={{
          display: "inline-flex", alignItems: "center", gap: 4,
          background: "transparent", border: 0, padding: 0,
          font: "inherit", color: "inherit", cursor: "pointer",
          textAlign: "left",
        }}
      >
        <small style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 140 }}>
            {currentName || "Choose community"}
          </span>
          <Icon name="chevronDown" size={12} style={{ opacity: 0.6 }} />
        </small>
      </button>
      {open && (
        <div
          role="listbox"
          style={{
            position: "absolute",
            top: "calc(100% + 6px)",
            left: -4,
            minWidth: 220,
            background: "var(--surface)",
            border: "1px solid var(--hairline)",
            borderRadius: 10,
            boxShadow: "var(--shadow-lg)",
            padding: 4,
            zIndex: 50,
          }}
        >
          <div className="text-xs text-mute" style={{ padding: "8px 10px 6px", letterSpacing: 0.4, textTransform: "uppercase" }}>
            Your communities
          </div>
          {memberships.map((m) => {
            const isCurrent = m.is_active_community;
            return (
              <button
                key={m.community_id}
                type="button"
                role="option"
                aria-selected={isCurrent}
                onClick={() => { if (!isCurrent) { onPick(m.community_id); } setOpen(false); }}
                style={{
                  display: "flex", width: "100%", alignItems: "center", justifyContent: "space-between",
                  background: isCurrent ? "var(--bg-2)" : "transparent",
                  border: 0, padding: "8px 10px", borderRadius: 8,
                  cursor: isCurrent ? "default" : "pointer",
                  fontSize: 13.5, color: "var(--ink)",
                }}
              >
                <span style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 2 }}>
                  <span style={{ fontWeight: 500 }}>{m.name}</span>
                  <span className="text-xs text-mute" style={{ textTransform: "capitalize" }}>
                    {m.role.replace("_", " ")}
                    {m.state ? ` · ${m.state}` : ""}
                  </span>
                </span>
                {isCurrent && <Icon name="check" size={14} style={{ color: "var(--accent)" }} />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Brand({ size = "md" }) {
  const sizeMap = { sm: 22, md: 28, lg: 40 };
  return (
    <div className="row" style={{ gap: 10, alignItems: "center" }}>
      <Logo size={sizeMap[size]} />
      <div className={size === "lg" ? "auth__brand-name" : "side__brand-text"}>
        Community Compass
        <small>HOA · bylaw · clarity</small>
      </div>
    </div>
  );
}

/* ─── Icon set (line, 18px) ──────────────────────────────────────────────── */
function Icon({ name, size = 18, ...rest }) {
  const props = {
    width: size, height: size, viewBox: "0 0 24 24",
    fill: "none", stroke: "currentColor", strokeWidth: 1.6,
    strokeLinecap: "round", strokeLinejoin: "round",
    ...rest,
  };
  const paths = {
    chat: <><path d="M21 12a8 8 0 0 1-11.5 7.2L4 21l1.8-5.4A8 8 0 1 1 21 12Z" /></>,
    history: <><path d="M3 12a9 9 0 1 0 3-6.7" /><path d="M3 4v5h5" /><path d="M12 7v5l3 2" /></>,
    book: <><path d="M4 4.5A2.5 2.5 0 0 1 6.5 2H20v17H6.5A2.5 2.5 0 0 0 4 21.5v-17Z" /><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" /></>,
    upload: <><path d="M12 16V4" /><path d="m7 9 5-5 5 5" /><path d="M5 20h14" /></>,
    users: <><circle cx="9" cy="8" r="3.5" /><path d="M2.5 20a6.5 6.5 0 0 1 13 0" /><circle cx="17" cy="9" r="3" /><path d="M16 14a6 6 0 0 1 5.5 6" /></>,
    dash: <><rect x="3" y="3" width="7" height="9" rx="1.4" /><rect x="14" y="3" width="7" height="5" rx="1.4" /><rect x="14" y="12" width="7" height="9" rx="1.4" /><rect x="3" y="16" width="7" height="5" rx="1.4" /></>,
    building: <><path d="M3 21V8l9-5 9 5v13" /><path d="M3 21h18" /><path d="M9 21v-5h6v5" /><path d="M8 9h.01M12 9h.01M16 9h.01M8 13h.01M12 13h.01M16 13h.01" /></>,
    coin: <><circle cx="12" cy="12" r="9" /><path d="M14.5 8.5a3 3 0 0 0-5 1.5c0 3 5 2 5 5a3 3 0 0 1-5 1.5" /><path d="M12 6v2M12 16v2" /></>,
    trend: <><path d="M3 17l6-6 4 4 8-8" /><path d="M14 7h7v7" /></>,
    settings: <><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1Z" /></>,
    search: <><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></>,
    send: <><path d="m4 11 16-7-7 16-2-7-7-2Z" /></>,
    spark: <><path d="M12 2v4M4.93 4.93l2.83 2.83M2 12h4M4.93 19.07l2.83-2.83M12 18v4M16.24 16.24l2.83 2.83M22 12h-4M16.24 7.76l2.83-2.83" /></>,
    plus: <><path d="M12 5v14M5 12h14" /></>,
    check: <><path d="m5 12 5 5L20 7" /></>,
    info: <><circle cx="12" cy="12" r="9" /><path d="M12 8h.01M11 12h1v5h1" /></>,
    shield: <><path d="M12 2 4 5v7c0 5 3.5 8.5 8 10 4.5-1.5 8-5 8-10V5l-8-3Z" /></>,
    clock: <><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></>,
    chevron: <><path d="m9 6 6 6-6 6" /></>,
    chevronDown: <><path d="m6 9 6 6 6-6" /></>,
    arrow: <><path d="M5 12h14M13 5l7 7-7 7" /></>,
    quote: <><path d="M7 7h4v4c0 2-1 3-3 4M15 7h4v4c0 2-1 3-3 4" /></>,
    door: <><rect x="6" y="3" width="12" height="18" rx="1" /><path d="M14 12h.01" /></>,
    moon: <><path d="M21 12.8A9 9 0 1 1 11.2 3 7 7 0 0 0 21 12.8Z" /></>,
    bell: <><path d="M6 8a6 6 0 1 1 12 0c0 6 3 7 3 7H3s3-1 3-7" /><path d="M10 21a2 2 0 0 0 4 0" /></>,
    calendar: <><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M3 9h18" /><path d="M8 3v4" /><path d="M16 3v4" /></>,
    file: <><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6" /></>,
    download: <><path d="M12 4v12" /><path d="m7 11 5 5 5-5" /><path d="M5 20h14" /></>,
    eye: <><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12Z" /><circle cx="12" cy="12" r="3" /></>,
    home: <><path d="m3 11 9-8 9 8" /><path d="M5 9v12h14V9" /></>,
    sparkle: <><path d="M12 3v4M12 17v4M3 12h4M17 12h4M5.6 5.6l2.8 2.8M15.6 15.6l2.8 2.8M5.6 18.4l2.8-2.8M15.6 8.4l2.8-2.8" /></>,
    paperclip: <><path d="M21 12.5 12.5 21a5 5 0 1 1-7-7L15 4.5a3.5 3.5 0 0 1 5 5L10.5 19" /></>,
    logout: <><path d="M15 4h3a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-3" /><path d="M10 17l-5-5 5-5" /><path d="M15 12H5" /></>,
    menu: <><path d="M3 6h18M3 12h18M3 18h18" /></>,
    x: <><path d="M6 6l12 12M18 6L6 18" /></>,
    tag: <><path d="M20.6 12.6 12.7 20.5a1.7 1.7 0 0 1-2.4 0L3 13.2V3.5h9.7l7.9 7.9a1 1 0 0 1 0 1.2Z" /><circle cx="7.5" cy="7.5" r="1.5" /></>,
    image: <><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="9" cy="9" r="2" /><path d="m21 15-5-5L5 21" /></>,
    sold: <><path d="M9 12l2 2 4-4" /><circle cx="12" cy="12" r="9" /></>,
    edit: <><path d="M4 20h4l11-11-4-4L4 16v4Z" /><path d="m14 5 4 4" /></>,
    trash: <><path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M6 6l1 14a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2l1-14" /></>,
  };
  return <svg {...props}>{paths[name] || null}</svg>;
}

/* ─── Sidebar ────────────────────────────────────────────────────────────── */
const ROLE_SUBTITLE = {
  super_admin: "SaaS operator",
  company_admin: "Company admin",
  manager: "Community manager",
  homeowner: "Homeowner",
};

const ROLE_AVATAR_COLOR = {
  super_admin: "b",
  company_admin: "b",
  manager: "t",
  homeowner: "g",
};

function userInitials(user) {
  const src = (user?.full_name || user?.email || "").trim();
  if (!src) return "";
  const parts = src.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return src.slice(0, 2).toUpperCase();
}

function userDisplayName(user) {
  return (user?.full_name || "").trim() || (user?.email || "").split("@")[0];
}

function Sidebar({ role, route, onRoute, onLogout, isOpen, onClose, onRole, user, onSwitchCommunity }) {
  const items = useMemo(() => sidebarItemsFor(role, user), [
    role,
    user?.community?.active_bylaws_count,
    user?.community?.active_members_count,
    user?.chat_session_count,
    user?.unread_messages_count,
    // Re-render when household permissions change so a primary toggling
    // Town Square off for a secondary takes effect on next /me refresh.
    user?.permissions,
    // Re-render when the joined sub-communities list changes so the
    // sidebar's "My Sub-Community" section stays in sync after a
    // create / join / leave (issue #216 slice 2).
    user?.sub_communities,
  ]);
  const groups = useMemo(() => {
    const out = [];
    let cur = null;
    for (const it of items) {
      if (it.group) { cur = { label: it.group, items: [] }; out.push(cur); }
      else cur.items.push(it);
    }
    return out;
  }, [items]);

  // Prefer the real logged-in user; fall back to the role-demo blob so the
  // sidebar still has something to show in unauthenticated previews.
  const fallback = ME[role] || { name: "—", subtitle: "", init: "?", color: "g" };
  const name = user ? (userDisplayName(user) || fallback.name) : fallback.name;
  const initials = user ? (userInitials(user) || fallback.init) : fallback.init;
  const color = user ? (ROLE_AVATAR_COLOR[user.role] || fallback.color) : fallback.color;
  const subtitle = user
    ? [ROLE_SUBTITLE[user.role] || "Member", user.community?.name].filter(Boolean).join(" · ")
    : fallback.subtitle;
  const communityName = user?.community?.name || "";

  const memberships = user?.memberships || [];
  const hasMultiCommunity = memberships.length > 1;

  return (
    <aside className={`side ${isOpen ? "side--open" : ""}`}>
      <div className="side__brand">
        <Logo size={26} />
        <div className="side__brand-text">
          Community<br />Compass
          {hasMultiCommunity ? (
            <CommunitySwitcher
              memberships={memberships}
              currentName={communityName}
              onPick={(cid) => onSwitchCommunity && onSwitchCommunity(cid)}
            />
          ) : (
            communityName && <small>{communityName}</small>
          )}
        </div>
        <button className="side__close" onClick={onClose} aria-label="Close menu">
          <Icon name="x" size={18} />
        </button>
      </div>

      {/* Mobile-only role switcher inside drawer. Same role gate as the
          top-bar version: only show options the actual backend role
          outranks; hide entirely when there's only one option. */}
      {onRole && (() => {
        const allowed = allowedViewRoles(user);
        if (allowed.length <= 1) return null;
        return (
          <div className="side__role-mobile">
            <div className="side__group-label" style={{ padding: "0 8px 6px" }}>View as</div>
            <div className="top__role" style={{ width: "100%", justifyContent: "space-between" }}>
              {[
                ["homeowner", "Homeowner"],
                ["manager", "Manager / Admin"],
                ["admin", "Operator"],
              ].filter(([k]) => allowed.includes(k)).map(([k, label]) => (
                <button
                  key={k}
                  className={role === k ? "is-active" : ""}
                  onClick={() => { onRole(k); onClose?.(); }}
                  style={{ flex: 1 }}
                >{label}</button>
              ))}
            </div>
          </div>
        );
      })()}

      {groups.map((g) => (
        <div className="side__group" key={g.label}>
          <div className="side__group-label">{g.label}</div>
          {g.items.map((it) => (
            <button
              // `id` is the React key (must be unique among siblings);
              // `route` is what we actually navigate to, defaulting to
              // `id`. Lets two sidebar entries land on the same route
              // without colliding keys (codex P2 on PR #219 — the
              // "+N more" overflow row in My Sub-Community).
              key={it.id}
              className={`side__item ${route === (it.route || it.id) ? "side__item--active" : ""}`}
              onClick={() => { onRoute(it.route || it.id); onClose?.(); }}
            >
              <span className="side__item-ico"><Icon name={it.icon} /></span>
              <span>{it.label}</span>
              {it.badge && <span className="side__item-badge">{it.badge}</span>}
            </button>
          ))}
        </div>
      ))}

      <div className="side__foot">
        <button className="side__user" onClick={() => onRoute("settings")}>
          <span className={`avatar avatar--${color}`}>{initials}</span>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div className="side__user-name" style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{name}</div>
            <div className="side__user-role">{subtitle}</div>
          </div>
          <Icon name="settings" size={14} style={{ opacity: .55 }} />
        </button>
        <button className="side__item" onClick={onLogout} style={{ marginTop: 4 }}>
          <span className="side__item-ico"><Icon name="logout" /></span>
          <span>Sign out</span>
        </button>
      </div>
    </aside>
  );
}

// Build the sidebar items for a given role from the live user payload.
// Counts come from /api/auth/me (community.active_bylaws_count etc.) so
// they're always per-community. Badges omitted when the count is 0 or
// not knowable (e.g. admin "Communities" count requires a dashboard
// fetch we don't do here).
function _badgeStr(n) {
  if (n === null || n === undefined) return undefined;
  if (n === 0) return undefined;
  return String(n);
}
// Drops nav entries whose id matches a permission key the household
// primary has turned off for this member. Group headers (no id) and
// entries with no matching key pass through untouched. Codex P3
// follow-up on #208 — backend gate is still authoritative; this just
// keeps disabled secondaries from landing on a 403 by clicking the
// nav. Mapping is `id === permission_key` (e.g. "town-square" maps
// to "town_square") so we normalize dashes to underscores.
function _filterItemsByPermissions(items, user) {
  const perms = user?.permissions;
  if (!perms) return items;
  return items.filter((it) => {
    if (!it.id) return true;
    const key = it.id.replace(/-/g, "_");
    if (Object.prototype.hasOwnProperty.call(perms, key) && perms[key] === false) {
      return false;
    }
    return true;
  });
}
// Cap on how many joined groups land directly in the sidebar — beyond
// this we keep the nav short and the user clicks "All Sub-Communities"
// to see the rest. Membership in groups is uncapped (only creating is
// capped at 5), so a power-user could otherwise blow the nav apart.
const SUB_COMMUNITY_NAV_LIMIT = 5;

function _subCommunityNavItems(user) {
  // Returns the group header + "All Sub-Communities" link + (up to N)
  // direct joined-group links + (overflow indicator if > N). Returns
  // [] if `user.sub_communities` is absent (older /me payloads from a
  // previously cached login). The "All Sub-Communities" entry stays
  // even when the list is empty so first-time users have a way in.
  const subs = Array.isArray(user?.sub_communities) ? user.sub_communities : [];
  const items = [
    { group: "My Sub-Community" },
    { id: "sub-communities", label: "All Sub-Communities", icon: "users" },
  ];
  const visible = subs.slice(0, SUB_COMMUNITY_NAV_LIMIT);
  for (const g of visible) {
    items.push({
      id: `sub-community-${g.id}`,
      label: g.name,
      // The role pill ("Creator"/"Mod") doubles as a badge so the
      // user knows which groups they help run without opening them.
      icon: "users",
      badge: g.role === "creator" ? "★"
        : g.role === "moderator" ? "✓"
        : undefined,
    });
  }
  if (subs.length > SUB_COMMUNITY_NAV_LIMIT) {
    // Visual cue that more groups exist than the sidebar shows; clicks
    // route to the same "All Sub-Communities" list as the entry up
    // top. Distinct `id` is the React key (codex P2 on PR #219 — two
    // siblings with `id: "sub-communities"` would collide and cause
    // unstable reconciliation); `route` is the actual destination.
    items.push({
      id: "sub-communities-overflow",
      route: "sub-communities",
      label: `+${subs.length - SUB_COMMUNITY_NAV_LIMIT} more`,
      icon: "chevron",
    });
  }
  return items;
}

function sidebarItemsFor(role, user) {
  const bylaws = _badgeStr(user?.community?.active_bylaws_count);
  const members = _badgeStr(user?.community?.active_members_count);
  const sessions = _badgeStr(user?.chat_session_count);
  const unreadMessages = _badgeStr(user?.unread_messages_count);
  if (role === "manager") {
    // company_admin gets the manager nav PLUS a Portfolio link to
    // their PMC dashboard (PR 5c). uiRole() collapses company_admin
    // into "manager" so the rest of the experience stays the same.
    const isCompanyAdmin = user?.role === "company_admin";
    const items = [
      { group: "Community" },
      { id: "dashboard", label: "Dashboard", icon: "dash" },
      { id: "chat", label: "Consult Compass", icon: "chat" },
      { id: "projects", label: "Project Builder", icon: "spark" },
      { id: "bylaws-mgmt", label: "Bylaws", icon: "book", badge: bylaws },
      { id: "roster", label: "Roster", icon: "users", badge: members },
    ];
    if (isCompanyAdmin) {
      items.push(
        { group: "Portfolio" },
        { id: "portfolio", label: "Portfolio", icon: "building" },
      );
    }
    items.push(
      { group: "My Community" },
      { id: "messages", label: "Messages", icon: "chat", badge: unreadMessages },
      { id: "town-square", label: "Town Square", icon: "users" },
      { id: "events", label: "Calendar", icon: "calendar" },
      { id: "marketplace", label: "Marketplace", icon: "tag" },
      { id: "services", label: "Services", icon: "users" },
      { id: "assistance", label: "Request Assistance", icon: "spark" },
      ..._subCommunityNavItems(user),
      { group: "Account" },
      { id: "settings", label: "Settings", icon: "settings" },
    );
    return _filterItemsByPermissions(items, user);
  }
  if (role === "admin") {
    return _filterItemsByPermissions([
      { group: "Operator" },
      { id: "admin-overview", label: "Overview", icon: "dash" },
      { id: "admin-health", label: "Health", icon: "spark" },
      { id: "admin-communities", label: "Communities", icon: "building" },
      { id: "admin-users", label: "Users", icon: "users" },
      { id: "admin-revenue", label: "Revenue", icon: "coin" },
      { id: "admin-trends", label: "Trends", icon: "trend" },
      { group: "My Community" },
      { id: "messages", label: "Messages", icon: "chat", badge: unreadMessages },
      { id: "town-square", label: "Town Square", icon: "users" },
      { id: "events", label: "Calendar", icon: "calendar" },
      { id: "marketplace", label: "Marketplace", icon: "tag" },
      { id: "services", label: "Services", icon: "users" },
      { id: "assistance", label: "Request Assistance", icon: "spark" },
      ..._subCommunityNavItems(user),
      { group: "Account" },
      { id: "settings", label: "Settings", icon: "settings" },
    ], user);
  }
  // homeowner (default)
  return _filterItemsByPermissions([
    { group: "Compass" },
    { id: "chat", label: "Consult Compass", icon: "chat" },
    { id: "history", label: "My conversations", icon: "history", badge: sessions },
    { id: "projects", label: "Project Builder", icon: "spark" },
    { id: "quota", label: "Usage limits", icon: "dash" },
    { group: "Learn" },
    { id: "bylaws", label: "Bylaws library", icon: "book", badge: bylaws },
    { id: "directory", label: "Community directory", icon: "users" },
    { group: "My Community" },
    { id: "messages", label: "Messages", icon: "chat", badge: unreadMessages },
    { id: "town-square", label: "Town Square", icon: "users" },
    { id: "events", label: "Calendar", icon: "calendar" },
    { id: "marketplace", label: "Marketplace", icon: "tag" },
    { id: "services", label: "Services", icon: "users" },
    { id: "assistance", label: "Request Assistance", icon: "spark" },
    ..._subCommunityNavItems(user),
    { group: "Account" },
    { id: "settings", label: "Settings", icon: "settings" },
  ], user);
}

const ME = {
  homeowner: { name: "Alex Rivera", subtitle: "Homeowner · Unit 4B", init: "AR", color: "g" },
  manager: { name: "Marisol Chen", subtitle: "Community manager", init: "MC", color: "t" },
  admin: { name: "Sasha Lin", subtitle: "SaaS operator", init: "SL", color: "b" },
};

/* ─── Top bar ────────────────────────────────────────────────────────────── */
// Homeowner < Manager < Operator. A user can only switch to views their
// actual backend role outranks. company_admin lives at Manager scope.
function allowedViewRoles(user) {
  const r = user?.role;
  if (r === "super_admin") return ["homeowner", "manager", "admin"];
  if (r === "company_admin" || r === "manager") return ["homeowner", "manager"];
  return ["homeowner"];
}

function TopBar({ title, subtitle, role, onRole, showSwitcher = true, right = null, onMenu = null, user = null }) {
  const allowed = allowedViewRoles(user);
  const showRoleSwitcher = showSwitcher && allowed.length > 1;
  return (
    <div className="top">
      {onMenu && (
        <button className="top__menu" onClick={onMenu} aria-label="Open menu">
          <Icon name="menu" size={20} />
        </button>
      )}
      <h1 className="top__crumbs">
        {title}
        {subtitle && <span className="top__sub">{subtitle}</span>}
      </h1>
      <div className="top__spacer" />
      {right}
      {showRoleSwitcher && (
        <div className="top__role" title="View as another role — useful for reproducing reported issues">
          {[
            ["homeowner", "Homeowner"],
            ["manager", "Manager / Admin"],
            ["admin", "Operator"],
          ].filter(([k]) => allowed.includes(k)).map(([k, label]) => (
            <button
              key={k}
              className={role === k ? "is-active" : ""}
              onClick={() => onRole(k)}
            >{label}</button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─── Sparkline ──────────────────────────────────────────────────────────── */
function Sparkline({ values, w = 120, h = 32, color = "var(--accent)" }) {
  if (!values || values.length === 0) return null;
  const min = Math.min(...values), max = Math.max(...values);
  const range = max - min || 1;
  const step = w / (values.length - 1);
  const pts = values.map((v, i) => [i * step, h - ((v - min) / range) * (h - 4) - 2]);
  const d = pts.map((p, i) => (i === 0 ? `M${p[0]},${p[1]}` : `L${p[0]},${p[1]}`)).join(" ");
  const area = `${d} L${w},${h} L0,${h} Z`;
  const lastY = pts[pts.length - 1][1];
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`}>
      <path d={area} fill={color} opacity="0.13" />
      <path d={d} fill="none" stroke={color} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
      <circle cx={w} cy={lastY} r="2.5" fill={color} />
    </svg>
  );
}

/* ─── Stat tile ──────────────────────────────────────────────────────────── */
function Stat({ label, value, unit, delta, deltaLabel, spark, sparkColor }) {
  const isDown = typeof delta === "number" && delta < 0;
  return (
    <div className="stat">
      <div className="stat__label">{label}</div>
      <div className="stat__value">
        <span>{value}</span>
        {unit && <span className="unit">{unit}</span>}
      </div>
      {delta !== undefined && (
        <div className={`stat__delta ${isDown ? "stat__delta--down" : ""}`}>
          {isDown ? "▾" : "▴"} {typeof delta === "number" ? (Math.abs(delta) < 1 ? `${(delta * 100).toFixed(1)}%` : delta) : delta} <span className="text-mute" style={{ marginLeft: 4 }}>{deltaLabel || "vs last 30d"}</span>
        </div>
      )}
      {spark && (
        <div className="stat__spark">
          <Sparkline values={spark} w={240} h={32} color={sparkColor} />
        </div>
      )}
    </div>
  );
}

/* ─── Bigger trend chart ─────────────────────────────────────────────────── */
function TrendChart({ data, height = 220 }) {
  // data: array of {questions, tokens}
  const padX = 28, padY = 16;
  const W = 720, H = height;
  const qs = data.map((d) => d[0]);
  const ts = data.map((d) => d[1]);
  const maxQ = Math.max(...qs), minQ = Math.min(...qs);
  const maxT = Math.max(...ts), minT = Math.min(...ts);
  const x = (i) => padX + (i * (W - padX * 2)) / (data.length - 1);
  const yQ = (v) => H - padY - ((v - minQ) / (maxQ - minQ || 1)) * (H - padY * 2);
  const yT = (v) => H - padY - ((v - minT) / (maxT - minT || 1)) * (H - padY * 2);
  const lineQ = data.map((d, i) => `${i === 0 ? "M" : "L"}${x(i)},${yQ(d[0])}`).join(" ");
  const areaQ = `${lineQ} L${x(data.length - 1)},${H - padY} L${x(0)},${H - padY} Z`;
  const lineT = data.map((d, i) => `${i === 0 ? "M" : "L"}${x(i)},${yT(d[1])}`).join(" ");
  return (
    <div className="chart" style={{ height: H }}>
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none">
        <defs>
          <linearGradient id="g-accent" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="var(--accent)" stopOpacity="0.35" />
            <stop offset="100%" stopColor="var(--accent)" stopOpacity="0" />
          </linearGradient>
        </defs>
        {/* gridlines */}
        <g className="chart__grid">
          {[0, 0.25, 0.5, 0.75, 1].map((p, i) => (
            <line key={i} x1={padX} x2={W - padX} y1={padY + p * (H - padY * 2)} y2={padY + p * (H - padY * 2)} />
          ))}
        </g>
        <path d={areaQ} fill="url(#g-accent)" />
        <path d={lineQ} className="chart__line" />
        <path d={lineT} className="chart__line chart__line--secondary" />
        {data.map((d, i) => i % 5 === 0 ? (
          <circle key={i} cx={x(i)} cy={yQ(d[0])} r="3" className="chart__dot" />
        ) : null)}
        {/* x-axis labels */}
        <g className="chart__axis">
          {[0, 6, 13, 20, 27].map((i) => {
            const label = ["30d ago", "24d", "17d", "10d", "3d"];
            return <text key={i} x={x(i)} y={H - 2} textAnchor="middle">{label[[0, 6, 13, 20, 27].indexOf(i)]}</text>;
          })}
        </g>
      </svg>
      <div style={{ position: "absolute", top: 8, right: 8, display: "flex", gap: 14, fontSize: 12, color: "var(--mute)" }}>
        <span className="row" style={{ gap: 6 }}><span style={{ width: 10, height: 2, background: "var(--accent)", borderRadius: 2 }} /> Questions</span>
        <span className="row" style={{ gap: 6 }}><span style={{ width: 10, height: 2, background: "var(--secondary)", borderRadius: 2, borderTop: "1px dashed var(--secondary)" }} /> Tokens (K)</span>
      </div>
    </div>
  );
}

/* ─── Bylaw row (cards) ──────────────────────────────────────────────────── */
function BylawRow({ b, onOpen, manager = false }) {
  return (
    <div className="bylaw">
      <div className="bylaw__ico" />
      <div>
        <div className="bylaw__title">{b.title}</div>
        <div className="bylaw__meta">
          <span className="pill pill--green"><span className="pill__dot" /> {b.status}</span>
          <span style={{ margin: "0 8px", color: "var(--hairline-2)" }}>·</span>
          <span className="mono">v{b.version}</span>
          <span style={{ margin: "0 8px", color: "var(--hairline-2)" }}>·</span>
          {b.pages} pages · <span className="mono">{b.size}</span>
          <span style={{ margin: "0 8px", color: "var(--hairline-2)" }}>·</span>
          Updated {b.uploaded} by {b.by}
        </div>
      </div>
      <div className="row" style={{ gap: 8 }}>
        <button className="btn btn--sm btn--ghost" onClick={() => onOpen(b)}>
          <Icon name="eye" size={14} /> {manager ? "Open" : "Read"}
        </button>
        {manager && (
          <button className="btn btn--sm btn--ghost"><Icon name="history" size={14} /> History</button>
        )}
      </div>
    </div>
  );
}

/* ─── Banner ─────────────────────────────────────────────────────────────── */
function Banner({ icon = "info", children, tone = "secondary" }) {
  return (
    <div className="banner" style={tone === "accent" ? { background: "var(--accent-soft)", color: "var(--accent-ink)", borderColor: "color-mix(in oklab, var(--accent) 14%, transparent)" } : undefined}>
      <span className="banner__ico"><Icon name={icon} size={16} /></span>
      <div>{children}</div>
    </div>
  );
}

/* ─── Field ──────────────────────────────────────────────────────────────── */
function Field({ label, hint, children }) {
  return (
    <div className="field">
      {label && <label className="field__label">{label}</label>}
      {children}
      {hint && <div className="field__hint">{hint}</div>}
    </div>
  );
}

/* ─── Pager ──────────────────────────────────────────────────────────────── */
//
// Standard offset-pagination footer for any list view that consumes the
// `{limit, offset, total, has_more}` meta envelope from
// `Api._unwrapList`. Render below the list:
//
//   <Pager meta={state.meta} offset={offset} onChange={setOffset} />
//
// Caller owns the offset state — Pager just emits the next offset
// on click. No-op when `meta` is null (e.g. an endpoint that hasn't
// been enveloped yet, like /api/events) or when there's only one
// page. CODE_REVIEW P2 L268 follow-up.
function Pager({ meta, offset, onChange, busy = false }) {
  if (!meta) return null;
  const total = meta.total || 0;
  const limit = meta.limit || total || 1;
  const rawCur = Math.max(0, offset || 0);
  // Clamp displayed range to total (codex P2 on PR #156). If the
  // dataset shrinks while the user is on a later page (poll, concurrent
  // delete, etc), `rawCur` can exceed `total` — showing "Showing 51-30
  // of 30" is nonsense. Clamp the label to the last valid page; Prev
  // is still active so the user can navigate back to populated rows.
  const lastPageStart = total === 0 ? 0 : Math.max(0, Math.floor((total - 1) / limit) * limit);
  const cur = Math.min(rawCur, lastPageStart);
  // Hide the pager only when there's genuinely a single page (total
  // fits in limit) AND the caller's actual offset is 0. Using
  // `rawCur` here, not `cur`, keeps the pager reachable when total
  // shrinks below a stale offset — otherwise the user is stranded on
  // an empty page with the pager hidden (codex P1 follow-up on PR
  // #156).
  if (total <= limit && rawCur === 0) return null;
  const first = total === 0 ? 0 : cur + 1;
  const last = Math.min(cur + limit, total);
  const goPrev = () => {
    if (busy || rawCur === 0) return;
    onChange?.(Math.max(0, rawCur - limit));
  };
  const goNext = () => {
    if (busy || !meta.has_more) return;
    onChange?.(rawCur + limit);
  };
  return (
    <div className="row" style={{
      justifyContent: "space-between", alignItems: "center",
      padding: "12px 4px", color: "var(--mute)", fontSize: 13,
    }}>
      <div>Showing <span className="mono">{first}</span>–<span className="mono">{last}</span> of <span className="mono">{total}</span></div>
      <div className="row" style={{ gap: 8 }}>
        <button
          className="btn btn--ghost"
          onClick={goPrev}
          disabled={busy || rawCur === 0}
          aria-label="Previous page"
        >
          ← Prev
        </button>
        <button
          className="btn btn--ghost"
          onClick={goNext}
          disabled={busy || !meta.has_more}
          aria-label="Next page"
        >
          Next →
        </button>
      </div>
    </div>
  );
}

// --- Native-shell helpers ---
// True when running inside a Capacitor native shell (iOS or Android).
function isCapacitorNative() {
  try {
    const cap = window.Capacitor;
    if (!cap) return false;
    const platform = (typeof cap.getPlatform === "function" && cap.getPlatform()) || cap.platform;
    return platform === "ios" || platform === "android";
  } catch (_) { return false; }
}

export { Logo, Brand, Icon, Sidebar, TopBar, Sparkline, Stat, TrendChart, BylawRow, Banner, Field, Pager, ME, isCapacitorNative };
